import React, { useState, useEffect, useRef } from 'react';
import {
  X,
  Upload,
  Package,
  Loader,
  AlertCircle,
  CheckCircle
} from 'lucide-react';
import { toast } from 'react-hot-toast';
import marketplaceAPI from '../../services/marketplace.api';
import marketplaceService from '../../services/marketplace.service';
import useMarketplaceStore from '../../store/marketplaceStore';

const ProductModal = ({ isOpen, businessId, product, onClose, onSuccess }) => {
  // If modal is not open, don't render anything
  if (!isOpen) return null;
  
  const { 
    productServiceCategories,
    getProductServiceCategories
  } = useMarketplaceStore();
  
  const [isLoading, setIsLoading] = useState(false);
  const [uploadingImages, setUploadingImages] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState('');
  const [availableCategories, setAvailableCategories] = useState([]);
  const [loadingCategories, setLoadingCategories] = useState(true);
  
  // Use refs to track if data has been loaded
  const dataLoadedRef = useRef(false);
  const categoriesLoadedRef = useRef(false);
  const allowedLoadedRef = useRef(false);
  
  const [formData, setFormData] = useState({
    name: product?.name || '',
    description: product?.description || '',
    price: product?.price || '',
    quantity: product?.quantity || 0,
    images: product?.images || [],
    category: product?.category || ''
  });

  const [errors, setErrors] = useState({});

  // Direct API call functions - bypassing store to prevent re-renders
  const fetchAllowedCategoriesDirectly = async (businessId) => {
    try {
      const response = await marketplaceAPI.getAllowedProductCategories(businessId);
      return response.data || [];
    } catch (error) {
      console.error('Failed to fetch allowed categories:', error);
      return [];
    }
  };

  // Single effect to load all data - runs only once when modal opens
  useEffect(() => {
    if (!dataLoadedRef.current) {
      dataLoadedRef.current = true;
      loadAllData();
    }
  }, [businessId]);

  // Effect to set form data when product changes (for editing)
  useEffect(() => {
    if (product) {
      setFormData({
        name: product.name || '',
        description: product.description || '',
        price: product.price || '',
        quantity: product.quantity || 0,
        images: product.images || [],
        category: product.category || ''
      });
      
      if (product.category) {
        setSelectedCategory(product.category);
      }
    }
  }, [product]);

  const loadAllData = async () => {
    setLoadingCategories(true);
    try {
      // Load product service categories (only if empty)
      if (productServiceCategories.length === 0 && !categoriesLoadedRef.current) {
        categoriesLoadedRef.current = true;
        await getProductServiceCategories();
      }
      
      // Get allowed categories for this business directly (bypass store)
      let allowed = [];
      if (!allowedLoadedRef.current) {
        allowedLoadedRef.current = true;
        allowed = await fetchAllowedCategoriesDirectly(businessId);
      }
      
      // Filter product service categories to only allowed ones
      const filteredCategories = productServiceCategories.filter(cat => 
        allowed.includes(cat.id)
      );
      setAvailableCategories(filteredCategories);
      
      // If editing a product, set the selected category
      if (product?.category) {
        setSelectedCategory(product.category);
      }
    } catch (error) {
      console.error('Failed to load categories:', error);
      toast.error('Failed to load product categories');
    } finally {
      setLoadingCategories(false);
    }
  };

  const handleCategoryChange = (categoryId) => {
    setSelectedCategory(categoryId);
    handleInputChange('category', categoryId);
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: null }));
    }
  };

  const handleImageUpload = async (file) => {
    if (formData.images.length >= 3) {
      toast.error('Maximum 3 images allowed');
      return;
    }

    setUploadingImages(true);
    try {
      const result = await marketplaceService.uploadImage(file);
      if (result.success) {
        setFormData(prev => ({
          ...prev,
          images: [...prev.images, result.data.url].slice(0, 3)
        }));
        toast.success('Image uploaded successfully');
      }
    } catch (error) {
      console.error('Image upload error:', error);
      toast.error(error.response?.data?.error?.message || 'Failed to upload image');
    } finally {
      setUploadingImages(false);
    }
  };

  const handleRemoveImage = (index) => {
    setFormData(prev => ({
      ...prev,
      images: prev.images.filter((_, i) => i !== index)
    }));
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Product name is required';
    } else if (formData.name.length < 2) {
      newErrors.name = 'Product name must be at least 2 characters';
    } else if (formData.name.length > 100) {
      newErrors.name = 'Product name must be less than 100 characters';
    }

    if (!formData.price || formData.price <= 0) {
      newErrors.price = 'Valid price is required';
    }

    if (!formData.category) {
      newErrors.category = 'Product category is required';
    }

    if (formData.quantity < 0) {
      newErrors.quantity = 'Quantity cannot be negative';
    }

    if (formData.quantity > 999999) {
      newErrors.quantity = 'Quantity is too high';
    }

    if (formData.description && formData.description.length > 1000) {
      newErrors.description = 'Description must be less than 1000 characters';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      toast.error('Please fix the errors in the form');
      const firstError = document.querySelector('.border-red-300');
      if (firstError) {
        firstError.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
      return;
    }

    const timestamp = Date.now();
    const randomStr = Math.random().toString(36).substring(2, 6).toUpperCase();

    const submitData = {
      name: formData.name.trim(),
      description: formData.description?.trim() || null,
      category: formData.category,
      price: parseFloat(formData.price),
      quantity: parseInt(formData.quantity) || 0,
      images: formData.images,
      sku: `SKU-${timestamp}-${randomStr}`,
      barcode: `BAR-${timestamp}-${randomStr}`,
      status: 'ACTIVE'
    };

    setIsLoading(true);
    try {
      if (product) {
        await marketplaceAPI.updateProduct(product.id, submitData);
        toast.success('Product updated successfully');
      } else {
        await marketplaceAPI.createProduct(businessId, submitData);
        toast.success('Product added successfully');
      }
      onSuccess();
    } catch (error) {
      console.error('Product submission error:', error);
      
      if (error.response?.data?.details) {
        const serverErrors = {};
        error.response.data.details.forEach(err => {
          serverErrors[err.field] = err.message;
        });
        setErrors(serverErrors);
        toast.error('Please check the form for errors');
      } else {
        toast.error(error.response?.data?.error?.message || 'Failed to save product');
      }
    } finally {
      setIsLoading(false);
    }
  };

  // Render category selection
  const renderCategorySelection = () => {
    if (loadingCategories) {
      return (
        <div className="flex items-center justify-center py-8">
          <Loader className="h-6 w-6 text-primary-600 animate-spin" />
          <span className="ml-2 text-sm text-gray-500">Loading categories...</span>
        </div>
      );
    }

    if (availableCategories.length === 0) {
      return (
        <div className="bg-yellow-50 rounded-xl p-4 shadow-neomorph-inset border border-yellow-200">
          <div className="flex items-start">
            <AlertCircle className="h-5 w-5 text-yellow-600 mr-2 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-sm font-medium text-yellow-800">No categories available</p>
              <p className="text-xs text-yellow-700 mt-1">
                Your business doesn't have any product categories assigned yet. Please contact support.
              </p>
            </div>
          </div>
        </div>
      );
    }

    return (
      <div>
        <label className="block text-xs font-medium text-gray-600 mb-2">
          Product Category <span className="text-red-500">*</span>
        </label>
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 sm:gap-3">
          {availableCategories.map((cat) => (
            <button
              key={cat.id}
              type="button"
              onClick={() => handleCategoryChange(cat.id)}
              className={`p-3 rounded-xl text-left transition-all border ${
                selectedCategory === cat.id
                  ? 'bg-primary-50 border-primary-200 shadow-neomorph-inset'
                  : 'bg-white border-gray-200 shadow-neomorph hover:shadow-neomorph-hover'
              }`}
            >
              <div className="flex items-center space-x-2">
                <span className="text-lg">{cat.icon || '📦'}</span>
                <div className="flex-1 min-w-0">
                  <span className="text-sm font-medium text-gray-800 block truncate">
                    {cat.name}
                  </span>
                  {cat.description && (
                    <p className="text-xs text-gray-500 mt-0.5 line-clamp-1">
                      {cat.description}
                    </p>
                  )}
                </div>
                {selectedCategory === cat.id && (
                  <CheckCircle className="h-4 w-4 text-primary-600 flex-shrink-0" />
                )}
              </div>
            </button>
          ))}
        </div>
        {errors.category && (
          <p className="mt-1.5 text-xs text-red-600">{errors.category}</p>
        )}
      </div>
    );
  };

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-3 sm:p-4 overflow-y-auto">
      <div className="bg-[#E8ECF5] rounded-2xl shadow-neomorph max-w-2xl w-full my-4 border border-primary-100/30">
        {/* Header */}
        <div className="p-4 sm:p-6 border-b border-primary-100/50 sticky top-0 bg-[#E8ECF5] z-10 rounded-t-2xl">
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <div className="w-1 h-6 sm:h-8 bg-gradient-to-b from-blue-400 to-blue-600 rounded-full mr-2 sm:mr-3" />
              <div>
                <h3 className="text-base sm:text-xl font-bold text-gray-800">
                  {product ? 'Edit Product' : 'Add Product'}
                </h3>
                <p className="text-xs text-gray-500 mt-0.5 hidden sm:block">
                  {product ? 'Update product information' : 'Add a new product to your business'}
                </p>
              </div>
            </div>
            <button 
              onClick={onClose} 
              className="p-2 bg-white rounded-lg shadow-neomorph hover:shadow-neomorph-hover transition-all text-gray-500"
            >
              <X className="h-4 w-4 sm:h-5 sm:w-5" />
            </button>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="p-4 sm:p-6 space-y-4 sm:space-y-6">
          <div className="max-h-[50vh] sm:max-h-[60vh] overflow-y-auto space-y-4 sm:space-y-6 px-1">
            {/* Basic Info */}
            <div className="bg-white rounded-xl shadow-neomorph p-4 sm:p-5 border border-primary-100/30">
              <h4 className="text-sm font-semibold text-gray-800 mb-4 flex items-center">
                <div className="w-1 h-4 bg-gradient-to-b from-blue-400 to-blue-600 rounded-full mr-2" />
                Basic Information
              </h4>
              
              <div className="space-y-4">
                <div>
                  <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1.5 sm:mb-2">
                    Product Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => handleInputChange('name', e.target.value)}
                    className={`w-full px-3 sm:px-4 py-2 sm:py-2.5 bg-white rounded-lg shadow-neomorph-inset border ${
                      errors.name ? 'border-red-300' : 'border-primary-100/30'
                    } focus:outline-none focus:ring-2 focus:ring-blue-500/20 text-sm sm:text-base`}
                    placeholder="e.g., Premium Phone"
                    maxLength={100}
                  />
                  {errors.name && (
                    <p className="mt-1 text-xs text-red-600">{errors.name}</p>
                  )}
                </div>

                <div>
                  <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1.5 sm:mb-2">
                    Category <span className="text-red-500">*</span>
                  </label>
                  {renderCategorySelection()}
                </div>

                <div>
                  <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1.5 sm:mb-2">
                    Description
                  </label>
                  <textarea
                    value={formData.description}
                    onChange={(e) => handleInputChange('description', e.target.value)}
                    rows={3}
                    className={`w-full px-3 sm:px-4 py-2 sm:py-2.5 bg-white rounded-lg shadow-neomorph-inset border ${
                      errors.description ? 'border-red-300' : 'border-primary-100/30'
                    } focus:outline-none focus:ring-2 focus:ring-blue-500/20 resize-none text-sm sm:text-base`}
                    placeholder="Describe your product features, specifications, and benefits..."
                    maxLength={1000}
                  />
                  <div className="flex justify-between items-center mt-1.5 sm:mt-2">
                    <span className="text-[10px] sm:text-xs text-gray-500 bg-gray-50 px-1.5 sm:px-2 py-0.5 sm:py-1 rounded-lg">
                      {formData.description?.length || 0}/1000
                    </span>
                    {errors.description && (
                      <span className="text-[10px] sm:text-xs text-red-600">{errors.description}</span>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Pricing & Inventory */}
            <div className="bg-white rounded-xl shadow-neomorph p-4 sm:p-5 border border-primary-100/30">
              <h4 className="text-sm font-semibold text-gray-800 mb-4 flex items-center">
                <div className="w-1 h-4 bg-gradient-to-b from-green-400 to-green-600 rounded-full mr-2" />
                Pricing & Inventory
              </h4>
              
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1.5 sm:mb-2">
                    Price (₦) <span className="text-red-500">*</span>
                  </label>
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500 text-sm sm:text-base">₦</span>
                    <input
                      type="number"
                      value={formData.price}
                      onChange={(e) => handleInputChange('price', e.target.value)}
                      className={`w-full pl-7 sm:pl-8 pr-3 sm:pr-4 py-2 sm:py-2.5 bg-white rounded-lg shadow-neomorph-inset border ${
                        errors.price ? 'border-red-300' : 'border-primary-100/30'
                      } focus:outline-none focus:ring-2 focus:ring-green-500/20 text-sm sm:text-base`}
                      placeholder="0.00"
                      min="0"
                      step="0.01"
                    />
                  </div>
                  {errors.price && (
                    <p className="mt-1 text-xs text-red-600">{errors.price}</p>
                  )}
                </div>
                
                <div>
                  <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1.5 sm:mb-2">
                    Quantity
                  </label>
                  <input
                    type="number"
                    value={formData.quantity}
                    onChange={(e) => handleInputChange('quantity', e.target.value)}
                    className={`w-full px-3 sm:px-4 py-2 sm:py-2.5 bg-white rounded-lg shadow-neomorph-inset border ${
                      errors.quantity ? 'border-red-300' : 'border-primary-100/30'
                    } focus:outline-none focus:ring-2 focus:ring-green-500/20 text-sm sm:text-base`}
                    placeholder="0"
                    min="0"
                    max="999999"
                  />
                  {errors.quantity && (
                    <p className="mt-1 text-xs text-red-600">{errors.quantity}</p>
                  )}
                </div>
              </div>
            </div>

            {/* Images */}
            <div className="bg-white rounded-xl shadow-neomorph p-4 sm:p-5 border border-primary-100/30">
              <h4 className="text-sm font-semibold text-gray-800 mb-4 flex items-center">
                <div className="w-1 h-4 bg-gradient-to-b from-purple-400 to-purple-600 rounded-full mr-2" />
                Product Images
              </h4>
              
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-2 sm:gap-3">
                {formData.images.map((url, index) => (
                  <div key={index} className="relative group">
                    <img
                      src={url}
                      alt={`Product ${index + 1}`}
                      className="h-20 w-full rounded-xl object-cover border-2 border-primary-200"
                    />
                    <button
                      type="button"
                      onClick={() => handleRemoveImage(index)}
                      className="absolute -top-2 -right-2 bg-red-50 text-red-600 rounded-lg p-1 shadow-neomorph hover:shadow-neomorph-hover transition-all opacity-0 group-hover:opacity-100"
                      title="Remove image"
                    >
                      <X className="h-3 w-3" />
                    </button>
                    {index === 0 && (
                      <span className="absolute -bottom-5 left-1/2 transform -translate-x-1/2 bg-blue-600 text-white text-[9px] sm:text-xs px-1.5 sm:px-2 py-0.5 rounded-lg whitespace-nowrap shadow-md">
                        Main
                      </span>
                    )}
                  </div>
                ))}
                
                {formData.images.length < 3 && (
                  <label className="cursor-pointer">
                    <div className="h-20 w-full rounded-xl border-2 border-dashed border-primary-200 bg-gray-50 flex flex-col items-center justify-center hover:bg-gray-100 transition-all">
                      {uploadingImages ? (
                        <Loader className="h-5 w-5 text-primary-500 animate-spin" />
                      ) : (
                        <>
                          <Upload className="h-5 w-5 text-primary-400" />
                          <span className="text-[9px] sm:text-xs text-gray-500 mt-1">Add</span>
                        </>
                      )}
                    </div>
                    <input
                      type="file"
                      className="hidden"
                      accept="image/*"
                      onChange={(e) => {
                        const file = e.target.files[0];
                        if (file) handleImageUpload(file);
                      }}
                      disabled={uploadingImages}
                    />
                  </label>
                )}
              </div>
              <p className="text-[10px] sm:text-xs text-gray-500 mt-3 bg-gray-50 px-2 sm:px-3 py-1.5 sm:py-2 rounded-lg">
                Up to 3 images. First is main image. JPG, PNG, GIF (max 5MB)
              </p>
            </div>

            {/* Form Error Summary */}
            {Object.keys(errors).length > 0 && (
              <div className="bg-red-50 rounded-xl shadow-neomorph-inset p-4 border border-red-200">
                <div className="flex">
                  <div className="p-1 bg-red-100 rounded-lg mr-2 sm:mr-3">
                    <AlertCircle className="h-4 w-4 sm:h-5 sm:w-5 text-red-600" />
                  </div>
                  <div className="flex-1">
                    <h4 className="text-xs sm:text-sm font-semibold text-red-800">Please fix the following errors:</h4>
                    <ul className="mt-1.5 text-[10px] sm:text-xs text-red-700 space-y-0.5 list-disc list-inside">
                      {Object.values(errors).slice(0, 3).map((error, index) => (
                        <li key={index}>{error}</li>
                      ))}
                      {Object.keys(errors).length > 3 && (
                        <li>+ {Object.keys(errors).length - 3} more errors</li>
                      )}
                    </ul>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Actions */}
          <div className="flex flex-col sm:flex-row justify-end space-y-2 sm:space-y-0 sm:space-x-3 pt-4 pb-6 sm:pb-4 border-t border-primary-100/50 sticky bottom-0 bg-[#E8ECF5] rounded-b-2xl">
            <button
              type="button"
              onClick={onClose}
              className="order-2 sm:order-1 w-full sm:w-auto px-4 sm:px-6 py-2 bg-white text-gray-700 font-medium rounded-lg shadow-neomorph hover:shadow-neomorph-hover transition-all text-sm"
              disabled={isLoading}
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isLoading}
              className="order-1 sm:order-2 w-full sm:w-auto px-4 sm:px-6 py-2 bg-gradient-to-r from-blue-600 to-blue-700 text-white font-medium rounded-lg shadow-md shadow-blue-500/30 hover:shadow-lg transition-all flex items-center justify-center disabled:opacity-50 text-sm"
            >
              {isLoading ? (
                <>
                  <Loader className="h-4 w-4 mr-2 animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <Package className="h-4 w-4 mr-2" />
                  {product ? 'Update Product' : 'Add Product'}
                </>
              )}
            </button>
          </div>
        </form>
      </div>

      <style jsx>{`
        .shadow-neomorph {
          box-shadow: 6px 6px 11px #d0d6e0, -6px -6px 11px #ffffff;
        }
        .shadow-neomorph-hover {
          box-shadow: 4px 4px 7px #d0d6e0, -4px -4px 7px #ffffff;
        }
        .shadow-neomorph-inset {
          box-shadow: inset 2.5px 2.5px 5px #d0d6e0, inset -2.5px -2.5px 5px #ffffff;
        }
      `}</style>
    </div>
  );
};

export default ProductModal;